import { firebaseFunction, db } from "../../firebase/firebase";
const crypto = import("crypto");
import { DB_CATEGORIES, IDbJobListingModel, IJobListingModel } from "ui";
import { IWorkshopModel, IWorkshopWithMentorModel } from "ui";
import type { IMentorModel } from "ui";

import {
  addDoc,
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  query,
  QuerySnapshot,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

export const getAllMentors = async () => {
  try {
    const mentorsCollection = collection(db, "users");
    const mentorsQuery = query(mentorsCollection, where("active", "==", true));
    const mentors = await getDocs(mentorsQuery);

    return mentors.docs.map((doc) => doc.data() /*as IMentorProfile*/);
  } catch (error) {
    return undefined;
  }
};

export const getWorkshopsByCategory = async (
  categories: typeof DB_CATEGORIES[number][],
  count: number,
  options: {
    excludeWorkshopWithId?: string;
    onlyActiveWorkshops?: boolean;
  }
) => {
  try {
    const workshopColRef = collection(db, "workshops");
    const workshopsQuery = query(
      workshopColRef,
      where("category", "array-contains-any", categories),
      where("active", "==", true),
      //orderBy("dateTime.startDate", "asc"), <-- need to remove this because of the freely bookable workshops
      limit(count)
    );

    const workshopDocs = await getDocs(workshopsQuery);

    return workshopDocs.docs
      .filter((doc) => {
        if (options.excludeWorkshopWithId !== undefined) {
          return doc.id !== options.excludeWorkshopWithId;
        }
        return doc;
      })
      .map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        } as IWorkshopModel;
      });
  } catch (error: unknown) {
    return undefined;
  }
};

export const workshopsWithMentor = (workshops: IWorkshopModel[]) => {
  try {
    const wsWithMentor = Promise.all(
      workshops.map(async (workshop) => {
        const mentor = (await getMentor(workshop.mentorId)) as IMentorModel;
        return {
          ...workshop,
          mentor: mentor || {},
        } as IWorkshopWithMentorModel;
      })
    );

    return wsWithMentor;
  } catch (error) {
    return undefined;
  }
};

export const getWorkshopsWithMentorByCategories = async (
  categories: typeof DB_CATEGORIES[number][],
  count: number,
  options?: {
    excludeCurrentWorkshopWithId?: string;
    onlyActive?: boolean;
  }
): Promise<IWorkshopWithMentorModel[] | undefined> => {
  try {
    const workshops = await getWorkshopsByCategory(categories, count, {
      excludeWorkshopWithId: options?.excludeCurrentWorkshopWithId,
    });

    if (!workshops) {
      return undefined;
    }

    const wsWithMentor = await workshopsWithMentor(workshops);

    if (!wsWithMentor) {
      return undefined;
    }

    return wsWithMentor;
  } catch (error: unknown) {
    return undefined;
  }
};

type FilterType = "category" | "mentor" | "hashtag" | "online" | "upcoming";

interface IFilteredWorkshopsWithMentor {
  type: FilterType;
  query: string | string[] | undefined; // fetches all workshops if query is undefined
  onlyActive: boolean;
}

type WorkshopWithId = {
  id: string;
} & IWorkshopModel;

export const getFilteredWorkshopsWithMentor = async (
  config: IFilteredWorkshopsWithMentor
) => {
  const { type, query: req, onlyActive } = config;
  let workshopColRef = collection(db, "workshops");
  let workshopQuery;
  let workshopDocs;

  try {
    const workshopsByType = {
      category: where("category", "array-contains-any", req),
      mentor: where("mentorId", "==", req),
      hashtag: where("hashtags", "array-contains", req),
      online: where("online", "==", req),
      upcoming: where("startDate", ">=", req),
    }[type];

    workshopQuery = query(workshopColRef, workshopsByType);

    if (onlyActive) {
      workshopQuery = query(workshopQuery, where("active", "==", true));
    }

    workshopDocs = await getDocs(workshopQuery);
  } catch (error) {
    return undefined;
  }

  const workshops = workshopDocs.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      } as WorkshopWithId)
  );

  const wsWithMentor = await workshopsWithMentor(workshops);
  if (!wsWithMentor) {
    return undefined;
  }
  return wsWithMentor;
};

export const getAllCategories = async () => {
  try {
    const allCategoriesRef = collection(db, "categories");
    const allCategoryDocs = await getDocs(query(allCategoriesRef));

    const categories = allCategoryDocs.docs.map((doc) => doc.data()); // TODO: name and value

    return categories;
  } catch (error) {
    return undefined;
  }
};

export const getWorkshops = async (id?: string) => {
  if (id !== undefined) {
    const workshopDocRef = doc(db, "workshops", id);
    const workshopDoc = await getDoc(workshopDocRef);

    if (!workshopDoc.exists()) {
      return undefined;
    }

    return { id: workshopDoc.id, ...workshopDoc.data() } as IWorkshopModel;
  }

  let workshopDocs: QuerySnapshot<DocumentData>;
  try {
    const workshopDocsRef = collection(db, "workshops");
    const workshopQuery = query(
      workshopDocsRef,
      where("active", "==", true)
      // orderBy("startDate", "desc") <-- need to remove this because of the freely bookable workshops
    );
    workshopDocs = await getDocs(workshopQuery);
  } catch (error) {
    return undefined;
  }

  return workshopDocs.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      } as IWorkshopModel)
  );
};

export const getMentor = async (id: string) => {
  try {
    const mentorDocRef = doc(db, "users", id);
    const mentor = (await (await getDoc(mentorDocRef)).data()) as IMentorModel;

    return mentor;
  } catch (error) {
    return undefined;
  }
};

export const sendBookingRequestEmail = async ({
  subject,
  preHeader,
  message,
  email,
  mentorId,
  firstName,
  lastName,
  company,
}: {
  subject: string;
  preHeader: string;
  message: string;
  email: string;
  mentorId: string;
  firstName: string;
  lastName: string;
  company: string;
}) => {
  const workshopRequestRef = httpsCallable(
    firebaseFunction,
    "sendWorkshopRequest"
  );
  return await workshopRequestRef({
    subject,
    preHeader,
    message,
    email,
    mentorId,
    firstName,
    lastName,
    company,
  })
    .then((result) => {
      return result;
    })
    .catch((error) => {
      return error;
    });
};

export const sendMentorWaitingListRequestEmail = async ({
  comment,
  email,
  firstName,
  lastName,
  linkedIn,
}: {
  comment: string;
  email: string;
  firstName: string;
  lastName: string;
  linkedIn: string;
}) => {
  const mentorWaitingListRequestRef = httpsCallable(
    firebaseFunction,
    "sendMentorWaitingListRequestEmail"
  );
  const subject = "[mentorlab] Neuer Mentorenanmeldung für die Warteliste!";
  const preHeader = "Bitte prüfen, ob Mentor geeignet ist";

  return await mentorWaitingListRequestRef({
    subject,
    preHeader,
    comment,
    email,
    firstName,
    lastName,
    linkedIn,
  })
    .then((result) => {
      return result;
    })
    .catch((error) => {
      return error;
    });
};

export const sendReportRequestEmail = async ({
  message,
  email,
  mentorId,
  mentorName,
  workshopId,
  workshopTitle,
  firstName,
  lastName,
  company,
}: {
  mentorName: string;
  workshopId: string;
  workshopTitle: string;
  message: string;
  email: string;
  mentorId: string;
  firstName: string;
  lastName: string;
  company: string;
}) => {
  const reportRequestRef = httpsCallable(
    firebaseFunction,
    "sendReportRequestEmail"
  );
  return await reportRequestRef({
    mentorName,
    workshopId,
    workshopTitle,
    message,
    email,
    mentorId,
    firstName,
    lastName,
    company,
  });
};

export const isVerifiedJobListingIssuer = async (id: string, token: string) => {
  try {
    const docRef = doc(db, "verifyJobListingsIssuer", id);

    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error(`Job listing issuer with id ${id} does not exist`);
    }
    const { token: issuerToken } = docSnap.data() as { token: string };

    if (token !== issuerToken) {
      return false;
    }

    return true;
  } catch (error) {
    throw error;
  }
};

export const verifyJobListingsIssuer = async (id: string) => {
  const jobListingRef = httpsCallable(
    firebaseFunction,
    "verifyJobListingsIssuer"
  );

  try {
    return await jobListingRef({ id });
  } catch (error) {
    throw new Error(`Failed to set verify to TRUE for job listing id: ${id}`);
  }
};

export const createAndVerifyJobListing = async (
  jobListing: Omit<
    IJobListingModel,
    | "id"
    | "state"
    | "status"
    | "interestedMentors"
    | "accepted"
    | "verified"
    | "postedAt"
  >
) => {
  try {
    const createdJobListingId = await createJobListing(jobListing);
    await createJobListingIssuerVerification(createdJobListingId);
  } catch (error) {
    throw error;
  }
};

const createJobListing = async (
  jobListing: Omit<
    IJobListingModel,
    | "id"
    | "state"
    | "status"
    | "interestedMentors"
    | "accepted"
    | "verified"
    | "postedAt"
  >
) => {
  const newDbJobListing: Omit<IDbJobListingModel, "id"> = {
    ...jobListing,
    verified: false,
    accepted: false,
    status: "Available",
    interestedMentors: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    postedAt: null,
  };

  const jobListingCollection = collection(db, "jobListings");
  try {
    const createdJobListing = await addDoc(
      jobListingCollection,
      newDbJobListing
    );

    return createdJobListing.id;
  } catch (error) {
    throw new Error("Failed to create new job listing");
  }
};

const createJobListingIssuerVerification = async (id: string) => {
  try {
    const issuerToken = await createJobListingIssuerToken();
    await setDoc(doc(db, "verifyJobListingsIssuer", id), {
      token: issuerToken,
    });
  } catch (error) {
    throw error;
  }
};

const createJobListingIssuerToken = async () => {
  try {
    return (await crypto).randomBytes(32).toString("hex");
  } catch (error) {
    throw new Error("Failed to create crypto token");
  }
};

export const getJobListing = async (id: string) => {
  try {
    const jobListingDocRef = doc(db, "jobListings", id);
    const jobListingSnapshot = await getDoc(jobListingDocRef);

    if (jobListingSnapshot.exists()) {
      return jobListingSnapshot.data();
    } else {
      throw new Error("Job Listing does not exist");
    }
  } catch (error) {
    throw new Error(`Failed to get job listing with id: ${id}`);
  }
};

export const getVerifiedJobListings = async () => {
  const q = query(collection(db, "jobListings"), where("verified", "==", true));

  try {
    const querySnapshot = await getDocs(q);
    const jobListings = [];
    querySnapshot.forEach((doc) => {
      jobListings.push({
        id: doc.id,
        ...(doc.data() as Omit<IDbJobListingModel, "id">),
      });
    });
  } catch (error) {
    throw new Error("Failed to fetch verfied job listings");
  }
};

export const getVerifiedAndAcceptedJobListings = async () => {
  const q = query(
    collection(db, "jobListings"),
    where("verified", "==", true),
    where("accepted", "==", true)
  );

  try {
    const querySnapshot = await getDocs(q);
    const jobListings = [];
    querySnapshot.forEach((doc) => {
      jobListings.push({
        id: doc.id,
        ...(doc.data() as Omit<IDbJobListingModel, "id">),
      });
    });
  } catch (error) {
    throw new Error("Failed to fetch verfied and accepted job listings");
  }
};
